import type { ComponentProps } from "react";
import {
  MessageScroller as MessageScrollerPrimitive,
  useMessageScroller,
  useMessageScrollerScrollable,
  useMessageScrollerVisibility,
} from "@shadcn/react/message-scroller";
import IconButton from "../../../../design-system/primitives/components/IconButton/IconButton";
import cx from "../../../../lib/cx";
import styles from "./MessageScroller.css";

/**
 * shadcn/ui's Message Scroller (ui.shadcn.com/docs/components/radix/message-scroller),
 * taken over part for part: the same headless primitive does all the scroll
 * work — following the live edge, letting go when the reader scrolls away,
 * anchoring a new turn near the top — and only the styling differs, which is
 * vanilla-extract here instead of Tailwind classes.
 *
 * `@shadcn/react` asks for React 19 as a peer because its components accept
 * `ref` as a plain prop. We never pass one, and everything else it uses exists
 * in React 18.
 */

const MessageScrollerProvider = MessageScrollerPrimitive.Provider;

const MessageScroller = ({
  className,
  ...props
}: ComponentProps<typeof MessageScrollerPrimitive.Root>) => (
  <MessageScrollerPrimitive.Root
    className={cx(styles.root, className)}
    {...props}
  />
);

const MessageScrollerViewport = ({
  className,
  ...props
}: ComponentProps<typeof MessageScrollerPrimitive.Viewport>) => (
  <MessageScrollerPrimitive.Viewport
    className={cx(styles.viewport, className)}
    {...props}
  />
);

const MessageScrollerContent = ({
  className,
  ...props
}: ComponentProps<typeof MessageScrollerPrimitive.Content>) => (
  <MessageScrollerPrimitive.Content
    className={cx(styles.content, className)}
    {...props}
  />
);

const MessageScrollerItem = ({
  className,
  scrollAnchor = false,
  ...props
}: ComponentProps<typeof MessageScrollerPrimitive.Item>) => (
  <MessageScrollerPrimitive.Item
    scrollAnchor={scrollAnchor}
    className={cx(styles.item, className)}
    {...props}
  />
);

type MessageScrollerButtonProps = {
  direction?: "start" | "end";
  /** Defaults to a label matching the direction. */
  ariaLabel?: string;
};

const MessageScrollerButton = ({
  direction = "end",
  ariaLabel,
}: MessageScrollerButtonProps) => (
  <MessageScrollerPrimitive.Button
    direction={direction}
    // `IconButton` owns its className, so the placement and the enter/exit
    // transition sit on a wrapper and the primitive's props go to the button.
    render={({ className: _className, inert, ...buttonProps }, { active }) => (
      <div
        className={styles.button}
        data-active={active}
        data-direction={direction}
      >
        <IconButton
          {...(buttonProps as object)}
          // React 18 has no boolean `inert`: `true` is dropped with a warning,
          // the empty string is what writes the attribute.
          {...(inert ? { inert: "" } : null)}
          icon="arrow-down"
          size="small"
          variant="secondary"
          ariaLabel={
            ariaLabel ??
            (direction === "end" ? "Zum Ende scrollen" : "Zum Anfang scrollen")
          }
        />
      </div>
    )}
  />
);

export {
  MessageScrollerProvider,
  MessageScroller,
  MessageScrollerViewport,
  MessageScrollerContent,
  MessageScrollerItem,
  MessageScrollerButton,
  useMessageScroller,
  useMessageScrollerScrollable,
  useMessageScrollerVisibility,
};
